import { useState } from 'react';
import { Copy, Check, MessageCircle, Printer, Download, Share2, Mail, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { openSms, copyWithToast } from '@/lib/shareLaunch';
import { useUserCredits } from '@/hooks/useUserCredits';
import { useLanguage } from '@/i18n/LanguageContext';
import { getSenderPerspectiveLabel } from '@/lib/senderPerspective';
import { getAnnouncementShareUrl } from '@/lib/shareUrl';
import { buildShareLinks } from '@/lib/buildShareLinks';
import { getShareText } from '@/lib/shareText';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

const shareCopy = {
  en: {
    shareTitle: 'Share your message',
    shareBody: 'This creates a link you can share anywhere.',
    noAutoPost: 'Nothing is posted automatically.',
    footerReassurance: "You're always in control — you choose where and when it's shared.",
    nativeShare: 'Share',
    copyLink: 'Copy link',
    sendText: 'Send as text message',
    openSms: 'Open SMS app',
    printLabel: 'Download or print a copy for yourself',
    print: 'Print',
    savePdf: 'Save as PDF',
    keepsakeApplied: 'Keepsake-quality formatting applied',
    printGated: 'Print is available for Connection and Legacy plans',
    pdfGated: 'PDF download is available for Connection and Legacy plans',
    pdfHint: 'Print dialog opened — choose "Save as PDF" to download',
    linkCopied: 'Link copied',
    linkCopyFail: 'Failed to copy link',
    shareTextFallback: 'Someone has written something meaningful — take a look.',
    emailLabel: 'Send directly to their email',
    emailPlaceholder: 'Enter recipient email address',
    sendEmail: 'Send',
    emailSent: 'Email sent to your recipient!',
    emailFailed: 'Failed to send email. You can still share the link.',
    sendingEmail: 'Sending...',
    invalidEmail: 'Please enter a valid email address',
    shareOnSocial: 'Share on social media',
    shareOn: 'Share on',
  },
  es: {
    shareTitle: 'Comparte tu mensaje',
    shareBody: 'Esto crea un enlace que puedes compartir en cualquier lugar.',
    noAutoPost: 'Nada se publica automáticamente.',
    footerReassurance: 'Siempre tienes el control — tú decides dónde y cuándo se comparte.',
    nativeShare: 'Compartir',
    copyLink: 'Copiar enlace',
    sendText: 'Enviar como mensaje de texto',
    openSms: 'Abrir app de SMS',
    printLabel: 'Descargar o imprimir una copia para ti',
    print: 'Imprimir',
    savePdf: 'Guardar como PDF',
    keepsakeApplied: 'Formato de calidad keepsake aplicado',
    printGated: 'Imprimir está disponible para los planes Connection y Legacy',
    pdfGated: 'La descarga de PDF está disponible para los planes Connection y Legacy',
    pdfHint: 'Diálogo de impresión abierto — elige "Guardar como PDF" para descargar',
    linkCopied: 'Enlace copiado',
    linkCopyFail: 'Error al copiar el enlace',
    shareTextFallback: 'Alguien ha escrito algo significativo — échale un vistazo.',
    emailLabel: 'Enviar directamente a su email',
    emailPlaceholder: 'Ingresa el email del destinatario',
    sendEmail: 'Enviar',
    emailSent: '¡Email enviado a tu destinatario!',
    emailFailed: 'Error al enviar el email. Puedes compartir el enlace.',
    sendingEmail: 'Enviando...',
    invalidEmail: 'Por favor ingresa un email válido',
    shareOnSocial: 'Compartir en redes sociales',
    shareOn: 'Compartir en',
  },
};

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  publicSlug: string;
  recipientName: string;
  onShared?: () => void;
  announcementId?: string;
  template?: string;
  relationship?: string;
  announcement?: {
    message?: string;
    photo_urls?: string[] | null;
    relationship?: string;
    published_at?: string | null;
    created_at?: string;
  };
}

// Social platform icons
function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export function ShareDialog({ open, onOpenChange, publicSlug, recipientName, onShared, announcementId, template, relationship, announcement }: ShareDialogProps) {
  const [copied, setCopied] = useState(false);
  const [hasShared, setHasShared] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const { credits } = useUserCredits();
  const { language } = useLanguage();
  const { user } = useAuth();
  const sc = shareCopy[language] || shareCopy.en;
  const hasPurchasedBundle = credits?.has_purchased_bundle ?? false;
  const highestBundle = credits?.highest_bundle_purchased || null;
  const hasConnectionAccess = highestBundle === 'connection' || highestBundle === 'legacy';
  const hasLegacyAccess = highestBundle === 'legacy';

  const senderName = user?.user_metadata?.full_name || user?.user_metadata?.name || null;
  const personalizedShareText = getShareText(senderName, relationship);
  
  const shareUrl = getAnnouncementShareUrl(publicSlug);
  const socialLinks = buildShareLinks({ publicSlug, templateSlug: template, relationship, senderName });
  const shareLinkTarget = typeof window !== 'undefined' && window.self !== window.top ? '_top' : '_blank';

  const handleCopyLink = async () => {
    const ok = await copyWithToast(shareUrl, sc.linkCopied, sc.linkCopyFail);
    if (ok) {
      setCopied(true);
      setHasShared(true);
      onShared?.();
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: language === 'es' ? 'Un mensaje para ti' : 'A message for you',
          text: personalizedShareText,
          url: shareUrl,
        });
        setHasShared(true);
        onShared?.();
      } catch (err) {
        // User cancelled — don't dismiss
        if ((err as Error).name !== 'AbortError') {
          // Fallback to copy
          await handleCopyLink();
        }
      }
    } else {
      await handleCopyLink();
    }
  };

  const handleSendText = () => {
    openSms(`${personalizedShareText} ${shareUrl}`);
    setHasShared(true);
    onShared?.();
  };

  const handleSendEmail = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail.trim())) {
      toast.error(sc.invalidEmail);
      return;
    }
    setEmailSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-anonymous-email', {
        body: {
          announcementId,
          recipientEmail: recipientEmail.trim(),
          recipientName,
        },
      });
      if (error) throw error;
      toast.success(sc.emailSent);
      setRecipientEmail('');
      setHasShared(true);
      onShared?.();

      // Show remaining-count warning
      if (data && typeof data.remaining === 'number' && data.remaining <= 3) {
        setTimeout(() => {
          if (data.remaining === 0) {
            toast.warning("You've reached your daily limit of 5 anonymous messages. Your limit resets in 24 hours.");
          } else {
            toast.warning(`You have ${data.remaining} anonymous message${data.remaining === 1 ? '' : 's'} remaining today.`);
          }
        }, 1500);
      }
    } catch (err) {
      // Show the server's explanation (e.g. recipient/daily limits) when available.
      let serverMsg: string | null = null;
      try {
        const ctx = (err as { context?: Response })?.context;
        if (ctx && typeof ctx.json === 'function') {
          const body = await ctx.clone().json();
          if (ctx.status === 429 && typeof body?.error === 'string') serverMsg = body.error;
        }
      } catch { /* ignore */ }
      toast.error(serverMsg || sc.emailFailed);
    } finally {
      setEmailSending(false);
    }
  };

  const handlePlatformLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    url: string
  ) => {
    const isEmbedded = typeof window !== 'undefined' && window.self !== window.top;
    if (!isEmbedded) return;
    event.preventDefault();
    try {
      if (window.top) {
        window.top.location.href = url;
        return;
      }
    } catch { /* ignore */ }
    window.location.href = url;
  };

  const platforms = [
    { key: 'facebook' as const, label: 'Facebook', Icon: FacebookIcon },
    { key: 'x' as const, label: 'X', Icon: XIcon },
    { key: 'linkedin' as const, label: 'LinkedIn', Icon: LinkedInIcon },
    { key: 'whatsapp' as const, label: 'WhatsApp', Icon: WhatsAppIcon },
  ];

  const handlePrint = () => {
    if (!hasConnectionAccess) {
      toast.error(sc.printGated);
      return;
    }
    
    const printContent = generatePrintHtml(recipientName, announcement, hasLegacyAccess);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const handleDownloadPdf = () => {
    if (!hasConnectionAccess) {
      toast.error(sc.pdfGated);
      return;
    }
    
    const printContent = generatePrintHtml(recipientName, announcement, hasLegacyAccess);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      toast.success(sc.pdfHint);
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-primary" />
            {sc.shareTitle}
          </DialogTitle>
          <DialogDescription className="space-y-1">
            <span className="block">{sc.shareBody}</span>
            <span className="block font-medium text-foreground/80">{sc.noAutoPost}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Primary: Native share + Copy link */}
          <div className="space-y-3">
            {typeof navigator !== 'undefined' && 'share' in navigator && (
              <Button onClick={handleNativeShare} className="w-full gap-2">
                <Share2 className="h-4 w-4" />
                {sc.nativeShare}
              </Button>
            )}

            <div className="flex gap-2">
              <Input readOnly value={shareUrl} className="flex-1 bg-muted text-sm" />
              <Button onClick={handleCopyLink} variant="outline" size="icon" className="shrink-0">
                {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <Separator />

          {/* Email to recipient */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">{sc.emailLabel}</label>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder={sc.emailPlaceholder}
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                className="flex-1"
                onKeyDown={(e) => e.key === 'Enter' && handleSendEmail()}
              />
              <Button
                onClick={handleSendEmail}
                disabled={emailSending || !recipientEmail.trim()}
                size="icon"
                className="shrink-0"
              >
                {emailSending ? (
                  <Mail className="h-4 w-4 animate-pulse" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          <Separator />

          {/* Social Media Share */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">{sc.shareOnSocial}</label>
            <div className="grid grid-cols-2 gap-2">
              {platforms.map(({ key, label, Icon }) => (
                <Button key={key} variant="outline" className="gap-2 justify-start" asChild>
                  <a
                    href={socialLinks[key]}
                    target={shareLinkTarget}
                    rel="noopener noreferrer"
                    onClick={(event) => handlePlatformLinkClick(event, socialLinks[key])}
                    aria-label={`${sc.shareOn} ${label}`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </a>
                </Button>
              ))}
            </div>
          </div>

          <Separator />

          {/* SMS */}
          <div className="space-y-3">
            <Button onClick={handleSendText} variant="outline" className="w-full gap-2">
              <MessageCircle className="h-4 w-4" />
              {sc.openSms}
            </Button>
          </div>

          <Separator />

          {/* Print & PDF section */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">{sc.printLabel}</label>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" onClick={handlePrint} disabled={!hasConnectionAccess} className="gap-2 relative">
                <Printer className="h-4 w-4" /> {sc.print}
                {!hasConnectionAccess && (
                  <Badge variant="outline" className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0">Connection</Badge>
                )}
              </Button>
              <Button variant="outline" onClick={handleDownloadPdf} disabled={!hasConnectionAccess} className="gap-2 relative">
                <Download className="h-4 w-4" /> {sc.savePdf}
                {!hasConnectionAccess && (
                  <Badge variant="outline" className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0">Connection</Badge>
                )}
              </Button>
            </div>
            {hasConnectionAccess && hasLegacyAccess && (
              <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <Check className="h-3 w-3" /> {sc.keepsakeApplied}
              </p>
            )}
          </div>

        </div>

        {/* Footer reassurance */}
        <div className="border-t border-border pt-4">
          <p className="text-xs text-muted-foreground text-center">
            {sc.footerReassurance}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Generate print-friendly HTML
function generatePrintHtml(
  recipientName: string,
  announcement?: ShareDialogProps['announcement'],
  isPremium: boolean = false
): string {
  const publishedDate = announcement?.published_at 
    ? new Date(announcement.published_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : announcement?.created_at 
      ? new Date(announcement.created_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });

  const margins = isPremium ? '2cm' : '1.5cm';
  const fontScale = isPremium ? '1.1' : '1';

  const senderLabel = announcement?.relationship
    ? getSenderPerspectiveLabel(announcement.relationship)
    : null;
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>A message for ${recipientName} | Say It NOW</title>
      <style>
        @page {
          size: A4;
          margin: ${margins};
        }
        
        * {
          box-sizing: border-box;
        }
        
        body {
          font-family: Georgia, 'Times New Roman', serif;
          font-size: calc(14px * ${fontScale});
          line-height: 1.8;
          color: #1a1a1a;
          max-width: 100%;
          margin: 0;
          padding: 0;
          background: white;
        }
        
        .container {
          max-width: 600px;
          margin: 0 auto;
          padding: 2rem;
        }
        
        .header {
          text-align: center;
          margin-bottom: 3rem;
          padding-bottom: 2rem;
          border-bottom: 2px solid #e5e5e5;
        }
        
        .recipient-label {
          font-size: 0.75em;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: #666;
          margin-bottom: 0.5rem;
        }
        
        .recipient-name {
          font-size: 2em;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0;
        }
        
        .relationship {
          font-size: 0.9em;
          color: #888;
          font-style: italic;
          margin-top: 0.5rem;
        }
        
        .photos {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin: 2rem 0;
        }
        
        .photo {
          max-width: 150px;
          max-height: 150px;
          object-fit: cover;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        .message {
          font-size: 1.1em;
          line-height: 2;
          white-space: pre-wrap;
          margin: 2rem 0;
          text-align: ${isPremium ? 'justify' : 'left'};
        }
        
        .footer {
          margin-top: 4rem;
          padding-top: 2rem;
          border-top: 1px solid #e5e5e5;
          text-align: center;
          color: #999;
          font-size: 0.8em;
        }
        
        .date {
          font-size: 0.85em;
          color: #888;
        }
        
        .branding {
          margin-top: 1rem;
          font-size: 0.75em;
        }
        
        ${isPremium ? `
          .container {
            padding: 3rem;
          }
          
          .header {
            border-bottom: 3px double #d4a574;
          }
          
          .recipient-name {
            color: #4a3728;
          }
          
          .message {
            padding: 0 1rem;
            border-left: 3px solid #d4a574;
          }
          
          .footer {
            border-top: 2px solid #d4a574;
          }
        ` : ''}
        
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <p class="recipient-label">A Living Announcement For</p>
          <h1 class="recipient-name">${recipientName}</h1>
            ${senderLabel ? `<p class="relationship">From your ${senderLabel}</p>` : ''}
        </div>
        
        ${announcement?.photo_urls && announcement.photo_urls.length > 0 ? `
          <div class="photos">
            ${announcement.photo_urls.slice(0, 3).map(url => `
              <img src="${url}" alt="Photo" class="photo" />
            `).join('')}
          </div>
        ` : ''}
        
        <div class="message">
          ${announcement?.message || 'Your heartfelt message'}
        </div>
        
        <div class="footer">
          <p class="date">${publishedDate}</p>
          <p class="branding">Created with Say It NOW — While They're Still Here</p>
        </div>
      </div>
    </body>
    </html>
  `;
}
